import { createVideoFramePulse } from '@/utils/videoFramePulse';
import type { SubtitleOverlayPlayerRef } from '../../subtitleOverlayPlayer/SubtitleOverlayPlayer';

export interface MediaPlaybackManagerOptions {
    getSourceVideo: () => HTMLVideoElement | null;
    getSubtitleOverlayPlayer: () => SubtitleOverlayPlayerRef | null;
    getIsPlaying: () => boolean;
    getSubtitlePlayEndTime: () => number | null;
    driftThresholdSec: number;
    uiUpdateIntervalMs: number;
    /** 与左侧 rvfc 对齐的 UI 时间（写入 store，避免整页 React 订阅） */
    setPlaybackUiTime: (t: number) => void;
}

/**
 * 左右画面同步与高频播放时钟：rvfc 留在管理器内，仅在节流间隔更新 UI 时间。
 * 右侧 seek 使用 isSeeking 锁，seek 进行中忽略后续纠偏 seek，避免与 Hls/解码竞态。
 */
export class MediaPlaybackManager {
    private pulse: ReturnType<typeof createVideoFramePulse> | null = null;
    private isSeekingRight = false;
    private rightSeekFallbackTimer: ReturnType<typeof setTimeout> | null = null;
    private lastUiUpdateAt = 0;
    private detachListeners: (() => void) | null = null;

    constructor(private readonly options: MediaPlaybackManagerOptions) {}

    notifyRightSeekStarted(): void {
        this.isSeekingRight = true;
        this.scheduleRightSeekFallbackUnlock();
    }

    private scheduleRightSeekFallbackUnlock(): void {
        if (this.rightSeekFallbackTimer != null) {
            clearTimeout(this.rightSeekFallbackTimer);
        }
        this.rightSeekFallbackTimer = setTimeout(() => {
            this.isSeekingRight = false;
            this.rightSeekFallbackTimer = null;
        }, 220);
    }

    private clearRightSeekFallbackTimer(): void {
        if (this.rightSeekFallbackTimer != null) {
            clearTimeout(this.rightSeekFallbackTimer);
            this.rightSeekFallbackTimer = null;
        }
    }

    private readonly syncVisualFrame = ({ mediaTime }: { mediaTime: number }): void => {
        if (!this.options.getIsPlaying()) return;
        if (this.options.getSubtitlePlayEndTime() !== null) return;

        const right = this.options.getSubtitleOverlayPlayer();
        if (!right?.getCurrentTime || !right?.seekToTime) return;
        if (this.isSeekingRight) return;

        const rightTime = right.getCurrentTime() || 0;
        const drift = Math.abs(rightTime - mediaTime);
        if (drift > this.options.driftThresholdSec) {
            this.isSeekingRight = true;
            try {
                right.seekToTime(mediaTime);
            } catch {
                this.isSeekingRight = false;
            }
            this.scheduleRightSeekFallbackUnlock();
        }

        const now = Date.now();
        if (now - this.lastUiUpdateAt >= this.options.uiUpdateIntervalMs) {
            this.lastUiUpdateAt = now;
            this.options.setPlaybackUiTime(mediaTime);
        }
    };

    attach(): void {
        this.detach();

        const left = this.options.getSourceVideo();
        if (!left) return;

        const pulse = createVideoFramePulse(left, this.syncVisualFrame);
        this.pulse = pulse;

        const handlePlay = () => pulse.start();
        const handlePause = () => pulse.stop();
        const handleEnded = () => pulse.stop();

        if (!left.paused) {
            pulse.start();
        }
        left.addEventListener('play', handlePlay);
        left.addEventListener('pause', handlePause);
        left.addEventListener('ended', handleEnded);

        this.detachListeners = () => {
            pulse.stop();
            left.removeEventListener('play', handlePlay);
            left.removeEventListener('pause', handlePause);
            left.removeEventListener('ended', handleEnded);
            this.pulse = null;
        };
    }

    detach(): void {
        if (this.detachListeners) {
            this.detachListeners();
            this.detachListeners = null;
        }
    }

    destroy(): void {
        this.detach();
        this.clearRightSeekFallbackTimer();
        this.isSeekingRight = false;
    }
}
